import React from 'react';
import { motion } from 'framer-motion';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="hero" className="min-h-screen pt-32 sm:pt-40 lg:pt-48 pb-12 sm:pb-16 bg-gradient-to-b from-cream to-nude relative overflow-hidden" aria-label="Welcome to Century Longevity & Aesthetics">
      {/* Background decoration */}
      <motion.div 
        animate={{ 
          x: [0, -30, 0],
          y: [0, 30, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 20, 
          repeat: Infinity,
          ease: "easeInOut" 
        }}
        className="absolute top-0 right-0 w-[80%] h-[200%] -translate-y-1/2 translate-x-1/4 opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(201, 169, 97, 0.3) 0%, transparent 70%)'
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center relative z-10">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-olive-darkest leading-[1.1] mb-4 sm:mb-6 tracking-wide"
            >
              Welcome To
              <br />
              Century Longevity and Aesthetics!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-gold text-lg sm:text-xl md:text-2xl font-light mb-6 sm:mb-8 tracking-[0.15em] sm:tracking-[0.25em] uppercase"
            >
              Forest Hills, NY
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-olive-dark text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed max-w-xl"
            >
              Century - Longevity & Aesthetics is a premier medical spa in Forest Hills, NY offering advanced aesthetic treatments in a relaxing, luxurious setting. Our licensed medical professionals specialize in Botox, dermal fillers, laser skin rejuvenation, waxing, facials, body contouring, medical weight loss, and peptides. We combine cutting-edge technology with personalized care to help you look and feel your best. Book your consultation today and experience the perfect balance of beauty, wellness, and science.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6"
            >
              <motion.button
                whileHover={{ y: -2, boxShadow: '0 10px 30px rgba(201, 169, 97, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('contact')}
                className="bg-gold text-olive-darkest px-8 sm:px-10 py-3 sm:py-4 rounded font-semibold tracking-[0.15em] sm:tracking-[0.2em] uppercase text-sm hover:bg-gold/90 transition-all duration-300 shadow-lg w-full sm:w-auto text-center"
              >
                Begin Your Journey
              </motion.button>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('services')}
                className="bg-transparent text-olive-darkest px-8 sm:px-10 py-3 sm:py-4 rounded font-semibold tracking-[0.15em] sm:tracking-[0.2em] uppercase text-sm border-2 border-olive-darkest hover:bg-olive-darkest hover:text-cream transition-all duration-300 w-full sm:w-auto text-center"
              >
                Our Services
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative mt-8 lg:mt-0"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="w-full h-[450px] sm:h-[550px] lg:h-[600px] rounded-lg shadow-2xl relative overflow-hidden border-2 border-olive-medium/20"
            >
              {/* Video */}
              <video 
                src="/aesthetic.mov" 
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover object-center"
                aria-label="Century Longevity & Aesthetics medical spa treatments showcase"
              >
                <track kind="captions" />
              </video>
              
              {/* Subtle overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-olive-darkest/20 to-transparent pointer-events-none" />
              
              {/* Shine effect on hover */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-gold/20 to-transparent -translate-x-full"
                whileHover={{
                  translateX: '200%',
                  transition: { duration: 0.8, ease: "easeInOut" }
                }}
              />
            </motion.div>

            {/* Decorative element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="absolute -bottom-4 sm:-bottom-6 -right-4 sm:-right-6 w-24 h-24 sm:w-32 sm:h-32 border-2 border-gold/30 rounded-lg -z-10"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;